/**
 * Rename dialog. Lets the user name the document; the `.fadein` extension is
 * enforced so saved files land with the right type.
 */
import { useState } from "react";

export function stripFadeinExt(name: string): string {
  return name.replace(/\.fadein$/i, "");
}

export function ensureFadeinExt(name: string): string {
  return stripFadeinExt(name.trim()) + ".fadein";
}

export interface RenameDialogProps {
  initial: string;
  title: string;
  confirmLabel: string;
  onSubmit: (filename: string) => void;
  onClose: () => void;
}

export function RenameDialog({
  initial,
  title,
  confirmLabel,
  onSubmit,
  onClose,
}: RenameDialogProps) {
  const [name, setName] = useState(stripFadeinExt(initial));

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0) return; // keep dialog open until a name is given
    onSubmit(ensureFadeinExt(trimmed));
    onClose();
  };

  return (
    <div className="sp-modal-backdrop" onClick={onClose}>
      <div
        className="sp-modal"
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="sp-modal-title">{title}</h2>
        <label className="sp-field">
          <span className="sp-field-label">File name</span>
          <div className="sp-rename-row">
            <input
              className="sp-input"
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
                if (e.key === "Escape") onClose();
              }}
            />
            <span className="sp-rename-ext">.fadein</span>
          </div>
        </label>
        <div className="sp-modal-actions">
          <button className="sp-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="sp-btn sp-btn-primary"
            onClick={submit}
            disabled={name.trim().length === 0}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
